import type { IPolicy } from '@adornis/abac/core/types.js';
import { isNullish } from '@adornis/assert/isNullish.js';
import {
  ensureLoggedInPayload,
  type LoggedInEvaluationFunction,
} from '../../permissions/abac-payloads/checkLoggedInPayload.js';
import {
  MARTINUS_ABAC_PAYLOAD,
  type MartinusAbacPayload,
} from '../../permissions/abac-payloads/MartinusAbacPayload.js';
import { hasRoles } from '../../permissions/api/hasRoles.js';
import { Role } from '../../permissions/roles.js';

export enum UserListActions {
  /**
   * Can the user even see the list?
   */
  USER_LIST = 'USE_LIST',

  /**
   * Can the user use the full list features.
   * Includes filtering and viewing all fields and extended view features
   */
  USER_LIST_FULL_FEATURES = 'USER_LIST_FULL_FEATURES',

  USER_LIST_BOOKINGS_EDIT = 'USER_LIST_BOOKINGS_EDIT',
  USER_LIST_EMAIL_SEND = 'USER_LIST_EMAIL_SEND',

  USER_LIST_EXPORT_LODAS = 'USER_LIST_EXPORT_LODAS',
  USER_LIST_EXPORT_TICKET = 'USER_LIST_EXPORT_TICKET',
  USER_LIST_EXPORT_SEPA = 'USER_LIST_EXPORT_SEPA',
  USER_LIST_EXPORT_ABWESENHEIT = 'USER_LIST_EXPORT_ABWESENHEIT',
  USER_LIST_EXPORT_EDOO_SYS = 'USER_LIST_EXPORT_EDOO_SYS',
  USER_LIST_SEND_SUS_WAITLIST_UPDATE_LINKS = 'USER_LIST_SEND_SUS_WAITLIST_UPDATE_LINKS',
  USER_LIST_IMPORT_PERSON = 'USER_LIST_IMPORT_PERSON',
}

const UserListPolicyFunctions: Record<UserListActions, LoggedInEvaluationFunction<MartinusAbacPayload>> = {
  /**
   * Normal role check for the users.
   */
  [UserListActions.USER_LIST]: async request => {
    if (
      hasRoles({
        user: request.payload.currentUser,
        roles: [
          Role.ADMIN,
          Role.SPENDEN_ADMIN,
          Role.SCHULLEITER,
          Role.BUCHHALTUNG,
          Role.SEKRETAER,
          Role.SUS_BEWERBER_BEARBEITUNG,
          Role.MAV,
          Role.PSB,
          Role.MITARBEITER,
          Role.IT,
        ],
      })
    )
      return { allowed: true, reason: 'Nutzer hat eine Rolle mit Listen-Zugriff.' };

    return { allowed: false, reason: 'Nutzer hat keine Rolle mit Listen-Zugriff.' };
  },

  /**
   * The full features can only be used by a few selected internal roles, which work with the platform on an everyday basis
   */
  [UserListActions.USER_LIST_FULL_FEATURES]: async request => {
    if (
      hasRoles({
        user: request.payload.currentUser,
        roles: [Role.SPENDEN_ADMIN, Role.SCHULLEITER, Role.SEKRETAER, Role.PSB],
      })
    )
      return { allowed: true, reason: 'Nutzer hat eine Rolle mit erweiterten Listen-Funktionen.' };

    return { allowed: false, reason: 'Nutzer hat keine Rolle mit erweiterten Listen-Funktionen.' };
  },

  [UserListActions.USER_LIST_BOOKINGS_EDIT]: async request => {
    if (
      hasRoles({
        user: request.payload.currentUser,
        roles: [Role.BUCHHALTUNG, Role.SEKRETAER],
      })
    )
      return { allowed: true, reason: 'Nutzer hat eine Rolle mit Berechtigung zur Buchungsbearbeitung.' };

    return { allowed: false, reason: 'Nutzer hat keine Rolle mit Berechtigung zur Buchungsbearbeitung.' };
  },

  [UserListActions.USER_LIST_EMAIL_SEND]: async request => {
    if (
      hasRoles({
        user: request.payload.currentUser,
        roles: [Role.SCHULLEITER, Role.PSB],
      })
    )
      return { allowed: true, reason: 'Nutzer hat eine Rolle mit Berechtigung zum E-Mail-Versand.' };

    return { allowed: false, reason: 'Nutzer hat keine Rolle mit Berechtigung zum E-Mail-Versand.' };
  },

  [UserListActions.USER_LIST_EXPORT_LODAS]: async request => {
    if (
      hasRoles({
        user: request.payload.currentUser,
        roles: [Role.PSB],
      })
    )
      return { allowed: true, reason: 'Nutzer hat eine Rolle mit Berechtigung für LODAS-Export.' };

    return { allowed: false, reason: 'Nutzer hat keine Rolle mit Berechtigung für LODAS-Export.' };
  },

  [UserListActions.USER_LIST_EXPORT_TICKET]: async request => {
    if (
      hasRoles({
        user: request.payload.currentUser,
        roles: [Role.PSB],
      })
    )
      return { allowed: true, reason: 'Nutzer hat eine Rolle mit Berechtigung für Ticket-Export.' };

    return { allowed: false, reason: 'Nutzer hat keine Rolle mit Berechtigung für Ticket-Export.' };
  },

  [UserListActions.USER_LIST_EXPORT_SEPA]: async request => {
    if (
      hasRoles({
        user: request.payload.currentUser,
        roles: [Role.BUCHHALTUNG],
      })
    )
      return { allowed: true, reason: 'Nutzer hat eine Rolle mit Berechtigung für SEPA-Export.' };

    return { allowed: false, reason: 'Nutzer hat keine Rolle mit Berechtigung für SEPA-Export.' };
  },

  [UserListActions.USER_LIST_EXPORT_ABWESENHEIT]: async request => {
    if (
      hasRoles({
        user: request.payload.currentUser,
        roles: [Role.PSB],
      })
    )
      return { allowed: true, reason: 'Nutzer hat eine Rolle mit Berechtigung für Abwesenheits-Export.' };

    return { allowed: false, reason: 'Nutzer hat keine Rolle mit Berechtigung für Abwesenheits-Export.' };
  },

  /**
   * The edoo.sys export hands applicants over to the school administration software, so it belongs to whoever processes applications.
   */
  [UserListActions.USER_LIST_EXPORT_EDOO_SYS]: async request => {
    if (
      hasRoles({
        user: request.payload.currentUser,
        roles: [Role.SCHULLEITER, Role.SEKRETAER, Role.SUS_BEWERBER_BEARBEITUNG],
      })
    )
      return { allowed: true, reason: 'Nutzer hat eine Rolle mit Berechtigung für edoo.sys-Export.' };

    return { allowed: false, reason: 'Nutzer hat keine Rolle mit Berechtigung für edoo.sys-Export.' };
  },

  /**
   * Asking waiting applicants to update their application is part of processing applications.
   */
  [UserListActions.USER_LIST_SEND_SUS_WAITLIST_UPDATE_LINKS]: async request => {
    if (hasRoles({ user: request.payload.currentUser, roles: [Role.SUS_BEWERBER_BEARBEITUNG] }))
      return { allowed: true, reason: 'Nutzer bearbeitet SuS-Bewerbungen.' };

    return { allowed: false, reason: 'Nutzer bearbeitet keine SuS-Bewerbungen.' };
  },

  [UserListActions.USER_LIST_IMPORT_PERSON]: async () => {
    return { allowed: false, reason: 'Kein Nutzer außer Admins können Personen importieren.' };
  },
};

export const UserListPolicy: IPolicy<MartinusAbacPayload> = {
  name: 'UserListPolicy',
  acceptingPayloadTypes: [MARTINUS_ABAC_PAYLOAD],
  async evaluate(request) {
    /**
     * Check logged in user
     */
    request.payload = ensureLoggedInPayload(request.payload);

    /**
     * Execute the correct function for the action
     */
    const result = await UserListPolicyFunctions[request.payload.action]?.(request);

    if (isNullish(result)) return false;
    return result;
  },
};
